import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';

import Counter, { BoundedLogic, ListLogic, ZeroValueLogic } from 'components/Input/Counter';
import Postfix from 'components/Input/Postfix';
import Select from 'components/Input/Select';
import Label from 'components/Label';
import DefaultTooltip from 'components/Tooltip/Default';
import {
    GROUND_GARDEN_OPTIONS,
    GROUND_GARDEN_PRIVATE,
    LEVEL_COUNT_OPTIONS,
    OneToTenLogic,
} from 'constants/Global';
import { isApartment, isHouse } from 'helpers/FormInformations';
import { translate } from 'helpers/Translate';

/**
 * One to five logic for bathrooms count.
 * @type {Object}
 */
const OneToFiveLogic = BoundedLogic(1, 5);

/**
 * Logic for floor.
 * @type {Object}
 */
const RdcLogic = ZeroValueLogic('RDC');

/**
 * Logic for floor.
 * @type {Object}
 */
const OneLevelHouseLogic = ListLogic(LEVEL_COUNT_OPTIONS);

/**
 * Logic for floor count.
 * @type {Object}
 */
const OneToFourtyNineLogic = BoundedLogic(1, 49);

/**
 * Step of the form with the main informations about the property
 */
export default class PropertyInformations extends Component {
    /**
     * CustomEvent to return data needed by GTM
     */
    componentDidMount() {
        const event = document.createEvent('CustomEvent');
        event.initCustomEvent('ma-estima-propertyInformationsStepLanded', true, true, {
            city: this.props.values.city_name,
            propertyType: this.props.values._property_type,
        });
        document.dispatchEvent(event);
    }

    /**
     * React lifecycle.
     */
    render() {
        const { values } = this.props;

        return (
            <div data-step-facts>
                {isApartment(values) && (
                    <div className="row-container row-container--padding-bottom">
                        <div className="row-container__label">
                            <Label labelFor="area" text={translate('Surface Carrez')} />
                        </div>
                        <div className="row-container__field">
                            <Field
                                component={Postfix}
                                name="area"
                                type="number"
                                placeholder={translate('Surface')}
                                postfixSymbol={translate('m²')}
                                autoFocus={values.area === undefined}
                            />
                        </div>
                    </div>
                )}
                {isHouse(values) && (
                    <div>
                        <div className="row-container row-container--padding-top row-container--padding-bottom">
                            <div className="row-container__label">
                                <Label labelFor="area" text={translate('Surface habitable')} />
                            </div>
                            <div className="row-container__field">
                                <Field
                                    component={Postfix}
                                    name="area"
                                    type="number"
                                    placeholder={translate('Surface')}
                                    postfixSymbol={translate('m²')}
                                    autoFocus={values.area === undefined}
                                />
                            </div>
                        </div>
                        <div className="row-container row-container--padding-bottom">
                            <div className="row-container__label">
                                <Label
                                    labelFor="site_area"
                                    text={translate('Surface totale du terrain')}
                                    subText={translate('(bâti inclus)')}
                                />
                            </div>
                            <div className="row-container__field">
                                <Field
                                    component={Postfix}
                                    name="site_area"
                                    type="number"
                                    placeholder={translate('Surface')}
                                    postfixSymbol={translate('m²')}
                                />
                            </div>
                        </div>
                        <div className="row-container row-container--padding-bottom">
                            <div className="row-container__label">
                                <Label
                                    labelFor="site_buildable_area"
                                    text={translate('Surface habitable encore constructible')}
                                />
                            </div>
                            <div className="row-container__field">
                                <Field
                                    component={Postfix}
                                    name="site_buildable_area"
                                    type="number"
                                    placeholder={translate('Surface')}
                                    postfixSymbol={translate('m²')}
                                />
                            </div>
                        </div>
                    </div>
                )}
                <div
                    className={`row-container row-container--padding-bottom${
                        isApartment(values) ? ' row-container--padding-top' : ''
                    }`}
                >
                    <div className="row-container__label">
                        <Label labelFor="room_count" text={translate('Nombre de pièces ')}>
                            <DefaultTooltip
                                tooltipId="roomCount"
                                tooltipText={translate(
                                    'Salle de bains, dressing, débarras et cuisine sont à exclure du nombre de pièces.',
                                )}
                            />
                        </Label>
                    </div>
                    <div className="row-container__field">
                        <Field component={Counter} name="room_count" fieldLogic={OneToTenLogic} />
                    </div>
                </div>
                <div className="row-container row-container--padding-bottom">
                    <div className="row-container__label">
                        <Label
                            labelFor="bathroom_count"
                            text={translate('Nombre de salles de bain ')}
                        >
                            <DefaultTooltip
                                tooltipId="bathroomCount"
                                tooltipText={translate(
                                    "Salle d'eau avec une baignoire ou une douche.",
                                )}
                            />
                        </Label>
                    </div>
                    <div className="row-container__field">
                        <Field
                            component={Counter}
                            name="bathroom_count"
                            fieldLogic={OneToFiveLogic}
                        />
                    </div>
                </div>

                {isApartment(values) && (
                    <div>
                        <div className="row-container row-container--padding-bottom row-container--padding-top">
                            <div className="row-container__label">
                                <Label labelFor="floor" text={translate('Étage de ce bien')} />
                            </div>
                            <div className="row-container__field">
                                <Field component={Counter} name="floor" fieldLogic={RdcLogic} />
                            </div>
                        </div>
                        <div className="row-container row-container--padding-bottom">
                            <div className="row-container__label">
                                <Label
                                    labelFor="floor_count"
                                    text={translate('Nombre d’étages dans l’immeuble')}
                                />
                            </div>
                            <div className="row-container__field">
                                <Field
                                    component={Counter}
                                    name="floor_count"
                                    fieldLogic={OneToFourtyNineLogic}
                                />
                            </div>
                        </div>

                        {values.floor === 0 && (
                            <div>
                                <div className="row-container row-container--padding-top row-container--padding-bottom">
                                    <div className="row-container__label">
                                        <Label
                                            labelFor="ground_garden"
                                            text={translate('Rez-de-jardin')}
                                        />
                                    </div>
                                    <div className="row-container__field">
                                        <Field
                                            component={Select}
                                            name="ground_garden"
                                            options={GROUND_GARDEN_OPTIONS}
                                        />
                                    </div>
                                </div>
                                {values.ground_garden === GROUND_GARDEN_PRIVATE && (
                                    <div className="row-container">
                                        <div className="row-container__label">
                                            <Label
                                                labelFor="ground_garden_area"
                                                text={translate('Surface jardin')}
                                            />
                                        </div>
                                        <div className="row-container__field">
                                            <Field
                                                component={Postfix}
                                                name="ground_garden_area"
                                                type="number"
                                                placeholder={translate('Surface')}
                                                postfixSymbol={translate('m²')}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {isHouse(values) && (
                    <div className="row-container row-container--padding-bottom">
                        <div className="row-container__label">
                            <Label labelFor="level_count" text={translate('Nombre de niveaux')} />
                        </div>
                        <div className="row-container__field">
                            <Field
                                component={Counter}
                                name="level_count"
                                fieldLogic={OneLevelHouseLogic}
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

PropertyInformations.displayName = 'PropertyInformations';

PropertyInformations.propTypes = {
    values: PropTypes.shape({}),
};
